import { memo } from 'react';
import PropTypes from 'prop-types';
import { isNil } from 'lodash';
import { useTranslation } from 'react-i18next';
// Import UI Components
import { SkeletonLoader, Tooltip, Icon } from '@geneui/components';
// Import Constants
import { l } from 'constants/common';

const ActivityKPI = ({ data, title, isLoading, withHeader }) => {
    const { t } = useTranslation();

    const {
        total,
        sendCount,
        sendRate,
        openCount,
        openRate,
        clickCount,
        clickRate,
        unsubscribeCount,
        clickUnsubscribeCount,
    } = data;

    return (
        <div className="crm-promotion-activity-kpi-wrapper">
            {withHeader && (
                <ul className="module-header cr-position-radius p-top s-big">
                    <li className="left-content">
                        <div className="left-inner">
                            <div className="module-title ellipsis-text" title="">
                                <div className="crm-statics-segment-data-table-name">{t(title)}</div>
                            </div>
                        </div>
                    </li>
                </ul>
            )}
            <div className="c-deep-view-block">
                <div className="c-deep-view-block-item color-blue">
                    <div className="c-deep-view-block-item-content">
                        <div className="c-deep-view-block-item-title-wrapper">
                            <p className="c-deep-view-block-item-title">{t(l.TotalCount)}</p>
                        </div>
                        <SkeletonLoader isBusy={isLoading}>
                            <p className="c-deep-view-block-item-subtitle">{isNil(total) ? t(l.NA) : total}</p>
                        </SkeletonLoader>
                    </div>
                </div>
                <div className="c-deep-view-block-item color-pink">
                    <div className="c-deep-view-block-item-content">
                        <div className="c-deep-view-block-item-title-wrapper">
                            <p className="c-deep-view-block-item-title">{t(l.SendCount)}</p>
                        </div>
                        <SkeletonLoader isBusy={isLoading}>
                            <p className="c-deep-view-block-item-subtitle">
                                {isNil(sendCount) ? t(l.NA) : sendCount}
                                {!isNil(sendRate) && `/${sendRate}%`}
                            </p>
                        </SkeletonLoader>
                    </div>
                </div>
                <div className="c-deep-view-block-item color-yellow">
                    <div className="c-deep-view-block-item-content">
                        <div className="c-deep-view-block-item-title-with-tooltip-wrapper">
                            <div className="c-deep-view-block-item-title-wrapper">
                                <p className="c-deep-view-block-item-title">{t(l.OpenCount)}</p>
                            </div>
                            <Tooltip text={t(l.OpenCountTooltipInfoMessage)}>
                                <Icon className="bc-icon-info-48" />
                            </Tooltip>
                        </div>
                        <SkeletonLoader isBusy={isLoading}>
                            <p className="c-deep-view-block-item-subtitle">
                                {isNil(openCount) ? t(l.NA) : openCount}
                                {!isNil(openRate) && `/${openRate}%`}
                            </p>
                        </SkeletonLoader>
                    </div>
                </div>
                <div className="c-deep-view-block-item color-green">
                    <div className="c-deep-view-block-item-content">
                        <div className="c-deep-view-block-item-title-wrapper">
                            <p className="c-deep-view-block-item-title">{t(l.ClickedCount)}</p>
                        </div>
                        <SkeletonLoader isBusy={isLoading}>
                            <p className="c-deep-view-block-item-subtitle">
                                {isNil(clickCount) ? t(l.NA) : clickCount}
                                {!isNil(clickRate) && `/${clickRate}%`}
                            </p>
                        </SkeletonLoader>
                    </div>
                </div>
                <div className="c-deep-view-block-item color-gray">
                    <div className="c-deep-view-block-item-content">
                        <div className="c-deep-view-block-item-title-with-tooltip-wrapper">
                            <div className="c-deep-view-block-item-title-wrapper">
                                <p className="c-deep-view-block-item-title">{t(l.UnsubscribeCount)}</p>
                            </div>
                            <Tooltip text={t(l.UnsubscribeCountTooltipInfoMessage)}>
                                <Icon className="bc-icon-info-48" />
                            </Tooltip>
                        </div>
                        <SkeletonLoader isBusy={isLoading}>
                            <p className="c-deep-view-block-item-subtitle">
                                {isNil(clickUnsubscribeCount) ? t(l.NA) : clickUnsubscribeCount}
                                {unsubscribeCount > 0 && `/${unsubscribeCount}`}
                            </p>
                        </SkeletonLoader>
                    </div>
                </div>
            </div>
        </div>
    );
};

ActivityKPI.propTypes = {
    data: PropTypes.object.isRequired,
    title: PropTypes.string,
    isLoading: PropTypes.bool,
    withHeader: PropTypes.bool,
};

ActivityKPI.defaultProps = {
    title: '',
    isLoading: false,
    withHeader: true,
};

export default memo(ActivityKPI);
